import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import styled from '@emotion/styled';

import { IProduct } from '~/lib/data-contract';
import { ProductComparePage } from '../product-compare-page';

type ProductCompareModalProps = {
    products: IProduct[];
    onClose: () => void;
    onRemoveProduct: (product: IProduct) => void;
};

export const ProductCompareModal = ({
                                        products,
                                        onClose,
                                        onRemoveProduct,
                                    }: ProductCompareModalProps) => {

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        document.addEventListener('keydown', handleKeyDown);

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = previousOverflow;
        };
    }, [onClose]);

    return createPortal(
        <Overlay onClick={onClose}>
            <Modal
                role="dialog"
                aria-modal="true"
                aria-label="Compare products"
                onClick={(event) => event.stopPropagation()}
            >
                <CloseButton
                    type="button"
                    onClick={onClose}
                    aria-label="Close comparison"
                >
                    ×
                </CloseButton>

                <ProductComparePage
                    products={products}
                    onRemoveProduct={onRemoveProduct}
                />
            </Modal>
        </Overlay>,
        document.body
    );
};

export default ProductCompareModal;

const Overlay = styled.div`
    position: fixed;
    inset: 0;
    z-index: ${({ theme }) => theme.zIndices['4']};

    display: flex;
    align-items: center;
    justify-content: center;

    padding: 24px;
    box-sizing: border-box;

    overflow: hidden;

    background: rgba(0, 0, 0, 0.55);
    backdrop-filter: blur(2px);

    @media (max-width: 768px), (max-height: 500px) {
        padding: 0;
        align-items: flex-start;
    }
`;


const Modal = styled.div`
    position: relative;

    width: min(1500px, 100%);
    height: min(900px, calc(100vh - 48px));
    padding-top: 20px;
    box-sizing: border-box;

    overflow-x: auto;
    overflow-y: auto;
    overscroll-behavior: contain;

    background: #fff;
    box-shadow: 0 16px 50px rgba(0, 0, 0, 0.3);

    @media (max-width: 768px), (max-height: 500px) {
        width: 100%;
        height: 100dvh;
        max-height: 100dvh;
        padding-bottom: env(safe-area-inset-bottom, 0px);
    }

    @media (max-width: 768px) {
        padding-top: 64px;
    }

    @media (max-height: 500px) {
        padding-top: 40px;
    }
`;

const CloseButton = styled.button`
    position: sticky;
    top: 12px;
    float: right;
    right: 12px;
    z-index: 1;

    display: flex;
    align-items: center;
    justify-content: center;

    width: 30px;
    height: 30px;

    padding: 0;
    border: 0;
    background: #fff;

    font-size: 28px;
    line-height: 1;
    cursor: pointer;

    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);

    &:hover {
        background: #f3f3f3;
    }
`;